import React, { useEffect, useState } from 'react';
import Arrow from '../FeaturedCatalogCarousel/Arrow';
import DiscoverPageSuggestion from './DiscoverPageSuggestion';
import { FormattedMessage } from 'react-intl';
import { getDiscoverPageSuggestions } from '@/redux/modules/discoverPageSuggestions';
import Slider from 'react-slick/lib/slider';
import styled from 'styled-components';
import Text from '@liveauctioneers/caterwaul-components/lib/Text/Text';
import { useSelector } from 'react-redux';

type Props = {
    className?: string;
};

type Suggestion = {
    imageURL: string;
    title: string;
};

const settings = {
    adaptiveHeight: false,
    centerPadding: 10,
    dots: false,
    infinite: false,
    initialSlide: 0,
    nextArrow: null,
    prevArrow: null,
    responsive: [
        { breakpoint: 992, settings: { slidesToScroll: 4, slidesToShow: 4 } },
        { breakpoint: 768, settings: { slidesToScroll: 2, slidesToShow: 2 } },
    ],
    slidesToScroll: 5,
    slidesToShow: 5,
    variableWidth: true,
};

const DiscoverPageSuggestionsCarousel = ({ className }: Props) => {
    settings.nextArrow = <StyledNext next />;
    settings.prevArrow = <StyledPrev prev />;

    const pageSuggestionsRedux = useSelector(getDiscoverPageSuggestions);
    const [discoverPageSuggestions, setDiscoverPageSuggestions] = useState(pageSuggestionsRedux);

    useEffect(() => {
        setDiscoverPageSuggestions(pageSuggestionsRedux);
    }, [pageSuggestionsRedux]);

    const mapDiscoverSuggestions = React.useCallback(
        ({ imageURL, title }: Suggestion) => <DiscoverPageSuggestion imageURL={imageURL} key={title} title={title} />,
        []
    );

    if (discoverPageSuggestions.length > 1) {
        return (
            <StyledDiscoverCarousel className={className}>
                <DiscoverPageSuggestionsScrollTitle h2>
                    <FormattedMessage id="discoverPageSuggestions.title" />
                </DiscoverPageSuggestionsScrollTitle>
                <StyledCarousel {...settings}>{discoverPageSuggestions.map(mapDiscoverSuggestions)}</StyledCarousel>
            </StyledDiscoverCarousel>
        );
    }
    return null;
};

export default React.memo<Props>(DiscoverPageSuggestionsCarousel);

const StyledDiscoverCarousel = styled.div`
    align-self: center;
    max-width: 1200px;
    overflow: hidden;
    width: calc(100% - 40px);
`;

const StyledCarousel = styled(Slider)`
    max-width: ${({ theme }) => `${theme.breakpoints.pageWidthNum}px`};
    width: calc(100% + 10px);
    align-self: center;
    margin: 0 0 16px 0;

    .slick-list > div {
        margin-left: 0;
        min-width: 100%;
    }
`;

const DiscoverPageSuggestionsScrollTitle = styled(Text)`
    display: block;
    margin: 20px 0;
`;

const StyledNext = styled(Arrow)`
    &&& {
        width: 40px;
        top: 50%;
        height: 100%;
        bottom: 0;
        position: absolute;
        background: rgba(255, 255, 255, 0.5);
        z-index: 1;
        color: ${({ theme }) => theme.colors.grey100};
        cursor: pointer;
        display: flex !important;
        justify-content: center;
        align-items: center;
        font-size: 30px;
        transition: all 0.15s ease-in;
        opacity: 0;
        right: 0;

        &:hover {
            color: ${({ theme }) => theme.colors.blue100};
            opacity: 0.8;
        }

        &::before {
            content: none;
        }

        @media (max-width: ${({ theme }) => `${theme.breakpoints.tabletNarrowWidthNum}px`}) {
            display: none !important;
        }
    }
`;
const StyledPrev = styled(Arrow)`
    &&& {
        width: 40px;
        top: 50%;
        height: 100%;
        bottom: 0;
        position: absolute;
        background: rgba(255, 255, 255, 0.5);
        z-index: 1;
        color: ${({ theme }) => theme.colors.grey100};
        cursor: pointer;
        display: flex !important;
        justify-content: center;
        align-items: center;
        font-size: 30px;
        transition: all 0.15s ease-in;
        opacity: 0;
        left: 0;

        &:hover {
            color: ${({ theme }) => theme.colors.blue100};
            opacity: 0.8;
        }

        &::before {
            content: none;
        }

        @media (max-width: ${({ theme }) => `${theme.breakpoints.tabletNarrowWidthNum}px`}) {
            display: none !important;
        }
    }
`;
